import { clamp01, fbm, mix, periodicNoise, rgb, seedOf } from './noise.js';

/**
 * What the building's surfaces look like, painted into pixels.
 *
 * Plain arrays and no three.js, so a surface can be painted, compared and
 * tested without a GPU. Every one tiles: `metres` is how much floor or wall one
 * copy of the texture covers, and the materials lay it down in world space so
 * the joints of adjacent rooms line up.
 *
 * The floor words are the ones the audio pass named — `concrete`, `tile`,
 * `carpet`, `marble`, `asphalt`, `gravel`, `vinyl` — so a surface looks like
 * what it sounds like. The ballroom's carpet is its own pattern, and the walls,
 * ceilings and props have theirs.
 *
 * Each painter returns `{ size, color, rough, metres }`: `color` is RGBA in
 * sRGB, `rough` is RGBA with roughness in the green channel (which is where
 * three.js reads it).
 */

const FT = 0.3048;

function canvas(size) {
  return { size, color: new Uint8Array(size * size * 4), rough: new Uint8Array(size * size * 4) };
}

/** Paints every pixel from `(u, v) => [r, g, b, roughness]`, u and v in [0, 1). */
function paint(size, metres, fn) {
  const out = canvas(size);
  for (let y = 0; y < size; y += 1) {
    for (let x = 0; x < size; x += 1) {
      const [r, g, b, ro] = fn(x / size, y / size, x, y);
      const i = (y * size + x) * 4;
      out.color[i] = r < 0 ? 0 : r > 255 ? 255 : r;
      out.color[i + 1] = g < 0 ? 0 : g > 255 ? 255 : g;
      out.color[i + 2] = b < 0 ? 0 : b > 255 ? 255 : b;
      out.color[i + 3] = 255;
      const rr = Math.round(clamp01(ro) * 255);
      out.rough[i] = rr;
      out.rough[i + 1] = rr;
      out.rough[i + 2] = rr;
      out.rough[i + 3] = 255;
    }
  }
  out.metres = metres;
  return out;
}

const shade = (c, k) => [c[0] * k, c[1] * k, c[2] * k];
const blend = (a, b, t) => [mix(a[0], b[0], t), mix(a[1], b[1], t), mix(a[2], b[2], t)];

/** Distance in [0, 0.5] to the nearest line of a grid of `n` cells across. */
function gridDistance(u, n) {
  const f = (u * n) % 1;
  return Math.min(f, 1 - f) / n;
}

/** Per-cell random in [0, 1), stable for a cell of an `n`-wide grid. */
function cellRandom(seed, cx, cy) {
  let h = (seed ^ Math.imul(cx + 7, 73856093) ^ Math.imul(cy + 13, 19349663)) >>> 0;
  h = Math.imul(h ^ (h >>> 16), 0x45d9f3b);
  h = Math.imul(h ^ (h >>> 16), 0x45d9f3b);
  return ((h ^ (h >>> 16)) >>> 0) / 4294967296;
}

// --- floors --------------------------------------------------------------------

function concrete(size = 256) {
  const n = fbm(seedOf('concrete'), { base: 4, octaves: 5 });
  const fine = periodicNoise(128, seedOf('concrete-fine'));
  const stain = fbm(seedOf('concrete-stain'), { base: 2, octaves: 3 });
  const base = rgb(0x8c8b86);
  return paint(size, 10 * FT, (u, v) => {
    let k = 0.86 + (n(u, v) - 0.5) * 0.28 + (fine(u * 128, v * 128) - 0.5) * 0.08;
    const s = stain(u, v);
    if (s > 0.62) k -= (s - 0.62) * 0.9;
    // Saw-cut control joints, one a panel.
    const joint = Math.min(gridDistance(u, 1), gridDistance(v, 1)) * size;
    if (joint < 1.2) k *= 0.62;
    return [...shade(base, k), 0.9 - (s > 0.62 ? 0.15 : 0)];
  });
}

function tile(size = 256) {
  // Twelve-inch grey ceramic, four across, with dark grout.
  const n = fbm(seedOf('tile'), { base: 8, octaves: 3 });
  return paint(size, 4 * FT, (u, v) => {
    const cx = Math.floor(u * 4);
    const cy = Math.floor(v * 4);
    const grout = Math.min(gridDistance(u, 4), gridDistance(v, 4)) * size;
    if (grout < 1.6) return [58, 60, 62, 0.95];
    const tone = 0.92 + cellRandom(11, cx, cy) * 0.12 + (n(u, v) - 0.5) * 0.08;
    // A faint bevel: tiles darken a little toward their edges.
    const edge = clamp01(grout / 6);
    return [...shade([150, 154, 156], tone * (0.9 + 0.1 * edge)), 0.35];
  });
}

function vinyl(size = 256) {
  // Vinyl composition tile, twelve inches, the break-room cream with speckle.
  const speck = periodicNoise(256, seedOf('vinyl-speck'));
  return paint(size, 4 * FT, (u, v) => {
    const cx = Math.floor(u * 4);
    const cy = Math.floor(v * 4);
    const seam = Math.min(gridDistance(u, 4), gridDistance(v, 4)) * size;
    const alt = (cx + cy) % 2 ? 0.95 : 1;
    const base = blend([196, 186, 160], [176, 168, 146], cellRandom(21, cx, cy) * 0.5);
    let c = shade(base, alt);
    const s = speck(u * 256, v * 256);
    if (s > 0.78) c = shade(c, 0.72);
    else if (s < 0.12) c = shade(c, 1.08);
    if (seam < 1) c = shade(c, 0.8);
    return [...c, 0.5];
  });
}

function officeCarpet(size = 256) {
  // Loop-pile commercial carpet: blue-grey tweed.
  const fine = periodicNoise(256, seedOf('office-carpet'));
  const mid = fbm(seedOf('office-carpet-mid'), { base: 16, octaves: 2 });
  return paint(size, 4 * FT, (u, v) => {
    const f = fine(u * 256, v * 256);
    const rows = 0.94 + 0.06 * Math.sin(v * size * Math.PI);
    const c = blend([74, 82, 98], [104, 112, 128], f * 0.7 + mid(u, v) * 0.3);
    return [...shade(c, rows), 1];
  });
}

function ballroomCarpet(size = 512) {
  /*
   * The ballroom's carpet: deep claret ground, a gold medallion on a navy
   * lozenge every six feet, and a scroll between them. Hotel ballroom carpet
   * is loud on purpose — it hides a banquet's worth of wine — and it is the
   * first thing that says this room is not the corridor.
   */
  const pile = periodicNoise(512, seedOf('ballroom-pile'));
  const claret = [112, 22, 38];
  const deep = [78, 14, 28];
  const navy = [26, 34, 70];
  const gold = [196, 152, 72];
  return paint(size, 6 * FT, (u, v) => {
    // Two medallions per tile, on a diamond lattice.
    const cells = [
      [0.5, 0.5],
      [0, 0],
      [1, 0],
      [0, 1],
      [1, 1],
    ];
    let best = 9;
    let cxm = 0;
    let cym = 0;
    for (const [cx, cy] of cells) {
      const d = Math.abs(u - cx) + Math.abs(v - cy);
      if (d < best) {
        best = d;
        cxm = cx;
        cym = cy;
      }
    }
    const dx = u - cxm;
    const dy = v - cym;
    const r = Math.hypot(dx, dy);
    const a = Math.atan2(dy, dx);
    let c = blend(claret, deep, 0.5 + 0.5 * Math.sin(u * Math.PI * 8) * Math.sin(v * Math.PI * 8));
    // The lozenge.
    if (best < 0.3) c = blend(c, navy, clamp01((0.3 - best) * 30));
    // Its gold border.
    if (Math.abs(best - 0.3) < 0.012) c = gold;
    // The medallion: eight petals and a ring.
    const petal = 0.1 + 0.05 * Math.cos(a * 8);
    if (r < petal) c = blend(gold, [150, 108, 48], r / petal);
    if (Math.abs(r - 0.17) < 0.008) c = gold;
    // Scrolls in the claret between lozenges.
    const scroll = Math.sin((u + v) * Math.PI * 12 + Math.sin((u - v) * Math.PI * 6) * 2);
    if (best > 0.34 && Math.abs(scroll) < 0.06) c = blend(c, gold, 0.55);
    const p = 0.9 + pile(u * 512, v * 512) * 0.18;
    return [...shade(c, p), 1];
  });
}

function marble(size = 512) {
  // Honed white marble in four-foot slabs, two across, with soft grey veining
  // drifting across them, each slab book-matched the other way.
  const warp = fbm(seedOf('marble'), { base: 2, octaves: 4 });
  const cloud = fbm(seedOf('marble-cloud'), { base: 2, octaves: 3 });
  return paint(size, 8 * FT, (u, v) => {
    const cx = Math.floor(u * 2);
    const cy = Math.floor(v * 2);
    const flip = cellRandom(31, cx, cy) > 0.5 ? 1 : -1;
    const w = warp(u, v);
    const vein = Math.abs(Math.sin((u * flip + v * 0.5) * Math.PI * 2 + w * 7));
    const thin = Math.pow(1 - vein, 9) * 0.55;
    const soft = Math.pow(1 - vein, 2.2) * 0.16;
    let c = blend([236, 233, 227], [218, 214, 206], cloud(u, v));
    c = blend(c, [138, 136, 134], clamp01(thin + soft));
    const joint = Math.min(gridDistance(u, 2), gridDistance(v, 2)) * size;
    if (joint < 1.1) c = shade(c, 0.86);
    return [...c, 0.1 + thin * 0.15];
  });
}

function asphalt(size = 256) {
  const aggregate = periodicNoise(256, seedOf('asphalt-agg'));
  const wear = fbm(seedOf('asphalt-wear'), { base: 3, octaves: 4 });
  const oil = fbm(seedOf('asphalt-oil'), { base: 2, octaves: 3 });
  return paint(size, 8 * FT, (u, v) => {
    const a = aggregate(u * 256, v * 256);
    let c = blend([46, 47, 50], [70, 70, 70], wear(u, v));
    if (a > 0.8) c = shade(c, 1.4);
    else if (a < 0.15) c = shade(c, 0.75);
    const o = oil(u, v);
    if (o > 0.66) c = shade(c, 1 - (o - 0.66) * 1.2);
    return [...c, 0.92];
  });
}

function gravel(size = 256) {
  /*
   * Decomposed granite: the buff-and-rust grit every yard in the Valley is
   * dressed in, with pebbles through it. The Caytons' "gravel" and the desert
   * outside the walls are the same ground.
   */
  const grit = periodicNoise(256, seedOf('dg-grit'));
  const patch = fbm(seedOf('dg-patch'), { base: 3, octaves: 4 });
  const pebbles = periodicNoise(32, seedOf('dg-pebbles'));
  return paint(size, 6 * FT, (u, v) => {
    let c = blend([170, 136, 100], [150, 112, 80], patch(u, v));
    const g = grit(u * 256, v * 256);
    c = shade(c, 0.86 + g * 0.26);
    const p = pebbles(u * 32, v * 32);
    if (p > 0.74) c = blend(c, p > 0.86 ? [110, 98, 90] : [196, 178, 150], clamp01((p - 0.74) * 8));
    return [...c, 1];
  });
}

function grass(size = 256) {
  /*
   * Overseeded lawn: the one green in the Valley anybody waters, cut short,
   * with the mower's stripes in it and the brown where the chairs stood.
   */
  const blades = periodicNoise(256, seedOf('lawn-blades'));
  const patch = fbm(seedOf('lawn-patch'), { base: 3, octaves: 4 });
  return paint(size, 10 * FT, (u, v) => {
    let c = blend([78, 112, 56], [98, 128, 62], patch(u, v));
    // Mower stripes, two to a tile.
    if (Math.floor(v * 4) % 2) c = shade(c, 1.08);
    c = shade(c, 0.86 + blades(u * 256, v * 256) * 0.28);
    const dry = patch(u * 2 + 0.3, v * 2 + 0.7);
    if (dry > 0.72) c = blend(c, [140, 128, 84], clamp01((dry - 0.72) * 3));
    return [...c, 1];
  });
}

function water(size = 256) {
  /*
   * The pool, seen from the deck: clear blue over a white plaster floor, with
   * the light rippling on the bottom. Painted, not simulated; the floor under
   * it is three and a half feet down and this is its surface.
   */
  const ripple = fbm(seedOf('pool-ripple'), { base: 6, octaves: 3 });
  const caustic = periodicNoise(64, seedOf('pool-caustic'));
  return paint(size, 8 * FT, (u, v) => {
    let c = blend([52, 150, 176], [80, 178, 196], ripple(u, v));
    const k = caustic(u * 64, v * 64);
    if (k > 0.7) c = blend(c, [200, 236, 240], clamp01((k - 0.7) * 2.2));
    return [...c, 0.15];
  });
}

// --- walls, ceilings, props ------------------------------------------------------

function block(size = 256) {
  // Painted concrete block, 16 by 8, running bond: back of house.
  const n = periodicNoise(128, seedOf('cmu'));
  return paint(size, 4 * FT, (u, v) => {
    const row = Math.floor(v * 6);
    const shifted = u * 3 + (row % 2 ? 0.5 : 0);
    const mortar = Math.min(gridDistance(v, 6), gridDistance(shifted / 3, 3)) * size;
    const k = 0.95 + (n(u * 128, v * 128) - 0.5) * 0.1;
    if (mortar < 1.8) return [...shade([150, 152, 150], 0.8), 0.9];
    return [...shade([158, 160, 156], k), 0.85];
  });
}

function plaster(size = 128, tint = [226, 220, 208]) {
  const n = fbm(seedOf('plaster'), { base: 4, octaves: 4 });
  return paint(size, 6 * FT, (u, v) => [...shade(tint, 0.95 + (n(u, v) - 0.5) * 0.08), 0.8]);
}

function travertine(size = 256) {
  // The lobby's walls: warm travertine in coursed slabs, banded.
  const band = fbm(seedOf('travertine'), { base: 2, octaves: 4 });
  const pits = periodicNoise(128, seedOf('travertine-pits'));
  return paint(size, 6 * FT, (u, v) => {
    const b = Math.sin(v * Math.PI * 10 + band(u, v) * 5);
    let c = blend([214, 196, 168], [196, 176, 146], 0.5 + 0.5 * b);
    if (pits(u * 128, v * 128) > 0.86) c = shade(c, 0.82);
    const joint = Math.min(gridDistance(u, 2), gridDistance(v, 3)) * size;
    if (joint < 1) c = shade(c, 0.78);
    return [...c, 0.35];
  });
}

function damask(size = 256) {
  /*
   * The ballroom's wall covering: tone-on-tone damask in warm taupe. An ogee
   * lattice — the onion-dome repeat every hotel ballroom has — with a small
   * mirrored flower in each cell, a shade lighter than the ground and a little
   * glossier, so it shows only when the light catches it.
   */
  const weave = periodicNoise(256, seedOf('damask-weave'));
  return paint(size, 3 * FT, (u, v) => {
    // Two cells across, one down; mirrored about each cell's centre line.
    const x = Math.abs(((u * 2) % 1) - 0.5);
    const y = v;
    const ogee = Math.abs(0.5 * Math.abs(Math.sin(y * Math.PI)) - x);
    const line = ogee < 0.018;
    // The flower, at the middle of the cell's height.
    const fy = y - 0.5;
    const r = Math.hypot(x * 1.6, fy);
    const a = Math.atan2(fy, x);
    const flower = r < 0.13 + 0.05 * Math.cos(a * 5) || (x < 0.02 && Math.abs(fy) < 0.3);
    const motif = line || flower;
    const base = motif ? [196, 180, 150] : [170, 154, 128];
    return [...shade(base, 0.96 + weave(u * 256, v * 256) * 0.06), motif ? 0.45 : 0.9];
  });
}

function stucco(size = 256) {
  // Outside walls: sand stucco, the colour of every wall in Scottsdale.
  const n = fbm(seedOf('stucco'), { base: 8, octaves: 4 });
  const fine = periodicNoise(256, seedOf('stucco-fine'));
  return paint(size, 6 * FT, (u, v) => {
    const k = 0.9 + (n(u, v) - 0.5) * 0.16 + (fine(u * 256, v * 256) - 0.5) * 0.12;
    return [...shade([196, 170, 136], k), 1];
  });
}

function acoustic(size = 256) {
  // Two-by-two lay-in ceiling tile in a white grid, fissured.
  const fissure = periodicNoise(128, seedOf('acoustic'));
  return paint(size, 4 * FT, (u, v) => {
    const grid = Math.min(gridDistance(u, 2), gridDistance(v, 2)) * size;
    if (grid < 2) return [232, 232, 230, 0.6];
    const f = fissure(u * 128, v * 128);
    return [...shade([214, 214, 210], f > 0.8 ? 0.84 : 0.97), 1];
  });
}

function wood(size = 256, tone = [150, 110, 72]) {
  // Planks, lengthwise along u, for pallets, crates, benches and the parquet.
  const grain = fbm(seedOf('wood'), { base: 2, octaves: 4 });
  return paint(size, 4 * FT, (u, v) => {
    const plank = Math.floor(v * 8);
    const g = Math.sin((u * 40 + grain(u, v) * 6 + plank * 3.1) * 2);
    let c = shade(tone, 0.86 + cellRandom(41, plank, 0) * 0.2 + g * 0.05);
    if (gridDistance(v, 8) * size < 1) c = shade(c, 0.6);
    return [...c, 0.7];
  });
}

function parquet(size = 256) {
  // The dance floor: basket-weave oak parquet, waxed.
  const grain = periodicNoise(64, seedOf('parquet'));
  return paint(size, 4 * FT, (u, v) => {
    const n = 8;
    const x = u * n;
    const y = v * n;
    const k = Math.floor(x) + Math.floor(y);
    const along = k % 2 ? x : y;
    const tone = 0.85 + cellRandom(51, Math.floor(x), Math.floor(y)) * 0.25;
    const g = grain((along * 8) % 64, (k * 7) % 64);
    let c = shade([176, 128, 78], tone * (0.94 + g * 0.1));
    const edge = Math.min(gridDistance(u, n), gridDistance(v, n)) * size;
    if (edge < 0.8) c = shade(c, 0.7);
    return [...c, 0.3];
  });
}

function brushed(size = 128, tone = [150, 156, 162]) {
  // Brushed steel: racks, the pass, the walk-in.
  const streak = periodicNoise(128, seedOf('brushed'));
  return paint(size, 3 * FT, (u, v) => [...shade(tone, 0.9 + streak(u * 4, v * 128) * 0.18), 0.35]);
}

export const PAINTERS = {
  concrete,
  tile,
  vinyl,
  carpet: officeCarpet,
  ballroomCarpet,
  marble,
  asphalt,
  gravel,
  grass,
  water,
  block,
  plaster: () => plaster(),
  ceilingPlaster: () => plaster(128, [236, 232, 224]),
  travertine,
  damask,
  stucco,
  acoustic,
  wood: () => wood(),
  darkWood: () => wood(256, [92, 60, 38]),
  parquet,
  steel: () => brushed(),
};

/** Paints a surface by name. Unknown names are concrete: every floor is something. */
export function paintSurface(name) {
  return (PAINTERS[name] ?? PAINTERS.concrete)();
}
